import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { navigate } from '@reach/router';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ArrowForward from '@mui/icons-material/ArrowForward';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Search from '@mui/icons-material/Search';
import { FeatureGlyph, FeatureStatusIcons } from './FeatureMarkers';
import { FeaturesHelpButton } from './FeaturesHelpGuide';
import { isPaidAddOn } from './PaidFeatureMark';
import { ActivateMark } from '../leftnav/NavLockedBubble';
import {
  displaySolutionTitle,
  fetchEntitlements,
  isComingSoonFeature,
  isFeatureVisibleToAccount,
  recordFeatureEvent,
} from '../../utils/productLicensing';
import './features.css';

const statusClassFor = (entitlement) => {
  if (!entitlement) return '';
  if (entitlement.reason === 'geo_blocked') return 'is-geo';
  if (entitlement.enabled) return 'is-enabled';
  return 'is-locked';
};

const statusLabel = (entitlement, t) => {
  if (!entitlement) return '';
  if (entitlement.reason === 'geo_blocked') {
    return t('Features.StatusGeo', { defaultValue: 'Not available here' });
  }
  if (entitlement.enabled) {
    return t('Features.StatusEnabled', { defaultValue: 'Enabled' });
  }
  return t('Features.StatusLocked', { defaultValue: 'Not enabled' });
};

const FeaturesList = ({ t }) => {
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState([]);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const boxId = localStorage.getItem('selectedLocation');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchEntitlements({
          boxId: boxId && boxId !== 'null' ? boxId : undefined,
        });
        if (!cancelled) {
          setEntitlements(
            ((data && data.entitlements) || []).filter(isFeatureVisibleToAccount)
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            t('Features.LoadError', {
              defaultValue: 'Unable to load features right now.',
            })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [boxId, t]);

  const hasGeoBlocked = entitlements.some(
    (feature) => feature.reason === 'geo_blocked'
  );
  const hasLocked = entitlements.some(
    (feature) => !feature.enabled && feature.reason !== 'geo_blocked'
  );
  const showStatusFilter = hasGeoBlocked || hasLocked;

  const filtered = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return entitlements.filter((feature) => {
      const title = displaySolutionTitle(
        feature.feature_key,
        feature.title
      ).toLowerCase();
      const tooltip = (feature.tooltip || '').toLowerCase();
      const matchesSearch =
        !query || title.includes(query) || tooltip.includes(query);
      if (!matchesSearch) return false;
      if (statusFilter === 'enabled') {
        return feature.enabled && feature.reason !== 'geo_blocked';
      }
      if (statusFilter === 'locked') {
        return !feature.enabled && feature.reason !== 'geo_blocked';
      }
      if (statusFilter === 'geo') {
        return feature.reason === 'geo_blocked';
      }
      return true;
    });
  }, [entitlements, searchText, statusFilter]);

  if (loading) {
    return (
      <div className="features-page">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="features-page">
        <p className="licensing-empty">{error}</p>
      </div>
    );
  }

  return (
    <div className="features-page">
      <div className="features-toolbar" data-tour="features-toolbar">
        <p className="features-toolbar-hint">
          {t('Features.Intro', {
            defaultValue:
              'Enabled features are ready to use. Request a locked feature with Talk to Expert — iLocalBox management reviews it offline, then the pharmacy is notified when it is available.',
          })}
        </p>
        <div className="features-toolbar-controls">
        {entitlements.length > 0 ? (
          <TextField
            className="features-search-field"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder={t('Features.SearchPlaceholder', {
              defaultValue: 'Search features...',
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        ) : null}
        {showStatusFilter ? (
          <FormControl
            className="features-status-field"
            variant="outlined"
            size="small"
          >
            <InputLabel id="features-status-filter-label">
              {t('Features.FilterStatus', { defaultValue: 'Status' })}
            </InputLabel>
            <Select
              labelId="features-status-filter-label"
              id="features-status-filter"
              label={t('Features.FilterStatus', { defaultValue: 'Status' })}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value || 'all')}
            >
              <MenuItem value="all">
                {t('Features.FilterAll', { defaultValue: 'All statuses' })}
              </MenuItem>
              <MenuItem value="enabled">
                {t('Features.StatusEnabled', { defaultValue: 'Enabled' })}
              </MenuItem>
              {hasLocked ? (
                <MenuItem value="locked">
                  {t('Features.StatusLocked', { defaultValue: 'Not enabled' })}
                </MenuItem>
              ) : null}
              {hasGeoBlocked ? (
                <MenuItem value="geo">
                  {t('Features.StatusGeo', { defaultValue: 'Not available here' })}
                </MenuItem>
              ) : null}
            </Select>
          </FormControl>
        ) : null}
        <FeaturesHelpButton t={t} />
        </div>
      </div>

      {entitlements.length === 0 ? (
        <p className="licensing-empty">
          {t('Features.Empty', {
            defaultValue:
              'No add-on features are listed for this pharmacy yet.',
          })}
        </p>
      ) : filtered.length === 0 ? (
        <p className="licensing-empty">
          {t('Features.NoMatches', {
            defaultValue: 'No features match this search.',
          })}
        </p>
      ) : (
        <div className="solutions-board">
          {[
            {
              key: 'ready',
              title: t('Solutions.Active', { defaultValue: 'Active' }),
              items: filtered.filter(
                (feature) =>
                  !!feature.enabled ||
                  !isComingSoonFeature(feature.feature_key, feature.title)
              ),
            },
            {
              key: 'soon',
              title: t('Solutions.ComingSoon', { defaultValue: 'Coming soon' }),
              items: filtered.filter(
                (feature) =>
                  !feature.enabled &&
                  isComingSoonFeature(feature.feature_key, feature.title)
              ),
            },
          ]
            .filter((section) => section.items.length > 0)
            .map((section, sectionIndex) => (
              <section key={section.key} className="solutions-section">
                <h2 className="solutions-section-title">{section.title}</h2>
                <div className="features-grid">
                  {section.items.map((feature, index) => {
                    const statusClass = statusClassFor(feature);
                    const solutionTitle = displaySolutionTitle(
                      feature.feature_key,
                      feature.title
                    );
                    const comingSoon =
                      !feature.enabled &&
                      isComingSoonFeature(feature.feature_key, feature.title);
                    const cardIndex = sectionIndex === 0 ? index : -1;
                    return (
                      <button
                        type="button"
                        key={feature.feature_key}
                        className={`feature-card ${statusClass}`.trim()}
                        data-tour={cardIndex === 0 ? 'features-card' : undefined}
                        onClick={() =>
                          navigate(`/home/features/${feature.feature_key}`)
                        }
                      >
                        <div className="feature-card-top">
                          <span className={`feature-card-icon ${statusClass}`.trim()}>
                            <FeatureGlyph featureKey={feature.feature_key} />
                          </span>
                          <div className="feature-card-heading">
                            <div className="feature-card-title-row">
                              <h3 className="feature-card-title">{solutionTitle}</h3>
                              {!feature.enabled && feature.reason !== 'geo_blocked' ? (
                                <ActivateMark
                                  label={t('Solutions.Activate', {
                                    defaultValue: 'Activate',
                                  })}
                                />
                              ) : null}
                            </div>
                            <div className="feature-card-meta">
                              <span className={`feature-card-status ${statusClass}`.trim()}>
                                {statusLabel(feature, t)}
                              </span>
                              {comingSoon ? (
                                <span className="solution-coming-soon">
                                  {t('Solutions.ComingSoon', {
                                    defaultValue: 'Coming soon',
                                  })}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <FeatureStatusIcons
                            entitlement={feature}
                            t={t}
                            onInfoViewed={() =>
                              recordFeatureEvent({
                                event: 'feature_tooltip_viewed',
                                feature_key: feature.feature_key,
                                location_id:
                                  boxId && boxId !== 'null' ? boxId : undefined,
                              })
                            }
                          />
                        </div>
                        <p className="feature-card-tooltip">{feature.tooltip}</p>
                        <div className="feature-card-foot">
                          {isPaidAddOn(feature) && !feature.enabled ? (
                            <p className="feature-card-paid">
                              <AttachMoney fontSize="inherit" />
                              <span>
                                {t('Features.PaidNote', {
                                  defaultValue:
                                    'Requires a paid subscription, speak with an expert to activate',
                                })}
                              </span>
                            </p>
                          ) : null}
                          <span
                            className="feature-card-cta"
                            data-tour={cardIndex === 0 ? 'features-talk' : undefined}
                          >
                            {!feature.enabled && feature.reason !== 'geo_blocked'
                              ? t('Features.TalkCta', {
                                  defaultValue: 'Talk to an expert',
                                })
                              : feature.enabled && feature.enabled_path
                                ? feature.feature_key === 'custom_brand_messaging'
                                  ? t('Features.OpenMessageControl', {
                                      defaultValue: 'Open Studio',
                                    })
                                  : feature.feature_key === 'campaigns'
                                    ? t('Features.OpenCampaigns', {
                                        defaultValue: 'Open Campaigns',
                                      })
                                    : t('Features.OpenFeature', {
                                        defaultValue: 'Open',
                                      })
                                : t('Features.LearnMore', {
                                    defaultValue: 'Learn more',
                                  })}
                            <ArrowForward fontSize="small" />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
        </div>
      )}
    </div>
  );
};

export default withTranslation()(FeaturesList);



// WEBPACK FOOTER //
// ./src/components/features/index.js